/** React Query hooks over `user-api.ts`: auth, profiles, roles, account administration. */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  authApi,
  usersApi,
  profilesApi,
  rolesApi,
  type User,
  type UserProfile,
  type LoginCredentials,
  type LoginResponse,
  type CreateUserData,
  type UpdateUserData,
  type UserQueryParams
} from '@/services/user-api';
import { getApiError } from '@/services/api-base';

// Re-export types for convenience
export type {
  User,
  UserProfile,
  LoginCredentials,
  LoginResponse,
  CreateUserData,
  UpdateUserData,
  UserQueryParams,
  UserSummary,
} from '@/services/user-api';

/**
 * Query keys for the user module
 */
export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: (filters: UserQueryParams = {}) => [...userQueryKeys.lists(), filters] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...userQueryKeys.details(), id] as const,
  search: (query: string, params: UserQueryParams = {}) => [...userQueryKeys.all, 'search', query, params] as const,
  summary: () => [...userQueryKeys.all, 'summary'] as const,
  current: () => [...userQueryKeys.all, 'current'] as const,
  permissions: () => [...userQueryKeys.all, 'permissions'] as const,
  profiles: {
    all: ['profiles'] as const,
    lists: () => [...userQueryKeys.profiles.all, 'list'] as const,
    detail: (id: number) => [...userQueryKeys.profiles.all, 'detail', id] as const,
    my: () => [...userQueryKeys.profiles.all, 'my'] as const,
  },
  roles: {
    all: ['roles'] as const,
    lists: () => [...userQueryKeys.roles.all, 'list'] as const,
  },
} as const;

/** Toast the API's message, or the fallback. */
const handleMutationError = (error: unknown, defaultMessage: string) => {
  const axiosError = error as AxiosError;
  const errorData = axiosError.response?.data as any;
  let errorMessage = defaultMessage;
  // Every /api/ error is the envelope {error: {code, message, details?}} (core/errors.py).
  const envelopeMessage = getApiError(error)?.message;
  if (envelopeMessage) errorMessage = envelopeMessage;
  else if (errorData) {
    if (errorData.detail) errorMessage = errorData.detail;
    else if (errorData.message) errorMessage = errorData.message;
    else if (errorData.errors) {
      // Collect first field error messages
      const fieldMsgs: string[] = [];
      Object.entries(errorData.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages) && messages.length) {
          fieldMsgs.push(`${field}: ${messages[0]}`);
        }
      });
      if (fieldMsgs.length) errorMessage = fieldMsgs.join('\n');
    }
  }
  toast.error(errorMessage);
  console.error('Mutation error detail:', errorData || error);
};

/** Toast, then invalidate the given keys. */
const handleMutationSuccess = (message: string, queryClient: QueryClient, keysToInvalidate: unknown[][]) => {
  toast.success(message);
  keysToInvalidate.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
};

// ==================== Authentication Hooks ====================

/**
 * Get current authenticated user
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: userQueryKeys.current(),
    queryFn: async () => {
      return authApi.getCurrentUser();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Login mutation hook
 */
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: (data: LoginResponse) => {
      toast.success('Login successful!');
      queryClient.setQueryData(userQueryKeys.current(), data.user);
      // Invalidate all user-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.profiles.all });
    },
    onError: (error) => handleMutationError(error, 'Login failed. Please check your credentials.'),
  });
};

/**
 * Logout mutation hook
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      toast.success('Logged out successfully');
      // Clear all cached data
      queryClient.clear();
      // Redirect to login page
      window.location.href = '/signin';
    },
    onError: (error) => {
      // Even if logout fails on server, clear local data
      queryClient.clear();
      handleMutationError(error, 'Logout completed with warnings');
      window.location.href = '/signin';
    },
  });
};

/**
 * Change password mutation hook
 */
export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { old_password: string; new_password: string; confirm_password: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      handleMutationSuccess(
        'Password changed successfully. You have been logged in with your new password.',
        queryClient,
        [[...userQueryKeys.current()]]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to change password'),
  });
};

/**
 * Request password reset mutation hook
 */
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
    onSuccess: () => {
      toast.success('Password reset instructions have been sent to your email');
    },
    onError: (error) => handleMutationError(error, 'Failed to send password reset email'),
  });
};

/**
 * Get user permissions hook
 */
export const useUserPermissions = () => {
  return useQuery({
    queryKey: userQueryKeys.permissions(),
    queryFn: () => authApi.getPermissions(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// ==================== User Management Hooks ====================

/**
 * Get paginated list of users with filtering
 */
export const useUsers = (params?: UserQueryParams) => {
  return useQuery({
    queryKey: userQueryKeys.list(params),
    queryFn: () => usersApi.getUsers(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Account-wide counts for the staff Users page (staff only)
 */
export const useUserSummary = () => {
  return useQuery({
    queryKey: userQueryKeys.summary(),
    queryFn: () => usersApi.getUserSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get specific user by ID
 */
export const useUser = (id: number) => {
  return useQuery({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => usersApi.getUser(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Search users hook
 */
export const useSearchUsers = (query: string, params?: UserQueryParams) => {
  return useQuery({
    queryKey: userQueryKeys.search(query, params),
    queryFn: () => usersApi.searchUsers(query, params),
    enabled: query.length >= 2, // Only search with at least 2 characters
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Create user mutation hook
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateUserData) => usersApi.createUser(userData),
    onSuccess: () => {
      handleMutationSuccess(
        'User created successfully',
        queryClient,
        [[...userQueryKeys.lists()]]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to create user'),
  });
};

/**
 * Update user mutation hook
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Use PATCH instead of PUT to avoid requiring full resource representation
    mutationFn: ({ id, userData }: { id: number; userData: UpdateUserData }) =>
      usersApi.patchUser(id, userData),
    onSuccess: (data, variables) => {
      handleMutationSuccess(
        'User updated successfully',
        queryClient,
        [
          [...userQueryKeys.lists()],
          [...userQueryKeys.detail(variables.id)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update user'),
  });
};

/**
 * Reset user password mutation hook (Admin only)
 */
export const useResetUserPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersApi.resetUserPassword(id),
    onSuccess: (data, variables) => {
      toast.success(`Password reset. A temporary password was e-mailed to ${data.delivered_to}.`);
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(variables) });
    },
    onError: (error) => handleMutationError(error, 'Failed to reset user password'),
  });
};

/**
 * Unlock user account mutation hook (admins; librarians for locked members below their role)
 */
export const useUnlockUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersApi.unlockUserAccount(id),
    onSuccess: (_, id) => {
      handleMutationSuccess(
        'User account unlocked successfully',
        queryClient,
        [
          [...userQueryKeys.lists()],
          [...userQueryKeys.detail(id)],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to unlock user account'),
  });
};

/**
 * Delete user mutation hook (Admin/Librarian only)
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => usersApi.deleteUser(id),
    onSuccess: () => {
      handleMutationSuccess(
        'User deleted successfully',
        queryClient,
        [
          [...userQueryKeys.lists()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to delete user'),
  });
};

// ==================== Profile Management Hooks ====================

/**
 * Get paginated list of profiles
 */
export const useProfiles = (params?: UserQueryParams) => {
  return useQuery({
    queryKey: userQueryKeys.profiles.lists(),
    queryFn: () => profilesApi.getProfiles(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get current user's profile
 */
export const useMyProfile = () => {
  return useQuery({
    queryKey: userQueryKeys.profiles.my(),
    queryFn: () => profilesApi.getMyProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) return false;
      return failureCount < 3;
    },
  });
};

/**
 * Get specific profile by ID
 */
export const useProfile = (id: number) => {
  return useQuery({
    queryKey: userQueryKeys.profiles.detail(id),
    queryFn: () => profilesApi.getProfile(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update current user's profile mutation hook
 */
export const useUpdateMyProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileData: Partial<UserProfile>) =>
      profilesApi.updateMyProfile(profileData),
    onSuccess: () => {
      handleMutationSuccess(
        'Profile updated successfully',
        queryClient,
        [
          [...userQueryKeys.profiles.my()],
          [...userQueryKeys.current()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update profile'),
  });
};

/**
 * Upload avatar image for current user
 */
export const useUploadMyAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => profilesApi.uploadMyAvatar(formData),
    onSuccess: (data) => {
      toast.success('Avatar updated');
      // Invalidate profile and current user caches so UI picks up new avatar
      queryClient.invalidateQueries({ queryKey: userQueryKeys.profiles.my() });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.current() });
    },
    onError: (error) => handleMutationError(error, 'Failed to upload avatar'),
  });
};

// ==================== Role Management Hooks ====================

/**
 * Get user roles
 */
export const useRoles = (params?: UserQueryParams) => {
  return useQuery({
    queryKey: userQueryKeys.roles.lists(),
    queryFn: () => rolesApi.getRoles(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Assign role mutation hook
 */
export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleData: { user_id: number; role: string }) =>
      rolesApi.assignRole(roleData),
    onSuccess: () => {
      handleMutationSuccess(
        'Role assigned successfully',
        queryClient,
        [
          [...userQueryKeys.roles.lists()],
          [...userQueryKeys.lists()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to assign role'),
  });
};

/**
 * Update role mutation hook
 */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roleData }: { id: number; roleData: Partial<{ role: string; is_active: boolean }> }) =>
      rolesApi.updateRole(id, roleData),
    onSuccess: () => {
      handleMutationSuccess(
        'Role updated successfully',
        queryClient,
        [
          [...userQueryKeys.roles.lists()],
          [...userQueryKeys.lists()],
        ]
      );
    },
    onError: (error) => handleMutationError(error, 'Failed to update role'),
  });
};

// ==================== Utility Hooks ====================

/**
 * Check if current user has specific permission
 */
export const useHasPermission = (permission: string) => {
  const { data: permissions } = useUserPermissions();
  return permissions?.permissions.includes(permission) || false;
};

/**
 * Check if current user has specific role
 */
export const useHasRole = (role: string) => {
  const { data: permissions } = useUserPermissions();
  return permissions?.roles.includes(role) || false;
};

/**
 * Get user's display name utility
 */
export const useUserDisplayName = (user?: User) => {
  if (!user) return '';
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.username;
};
